import { union } from "lodash";
import { Component, useContext } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { AdequacyQueryShape } from "@app/projects/CountyProjectContainer";
import { CountyProjectContext, CountyProjectContextProps } from "@app/projects/CountyProjectContext";
import { FilterType, ProjectPerspective, RegionSetStatusType } from "@app/projects/enumerators";
import ChangedGapsFilter from "@app/projects/network-assessment/ChangedGapsFilter";
import RegionStatusFilter from "@app/projects/network-assessment/RegionStatusFilter";
import SpecialtyFilter from "@app/projects/network-assessment/SpecialtyFilter";
import {
  getProjectPerspectiveDescription,
  getProjectPerspectiveFromFilterString
} from "@app/projects/projectPerspectiveConversions";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { QESRadioGroup } from "@app/shared/qes-standards/forms/RadioGroup";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import useCleanHistory, { CleanHistoryProps } from "@app/shared/useCleanHistory";
import { parseQuerystring } from "@app/shared/utility/parseQuerystring";
import { querystringify } from "@app/shared/utility/querystringify";

interface DetailsFiltersProps extends RouteComponentProps {
  availableRegionFilters?: RegionSetStatusType[];
  disabled?: boolean;
  enableChangedGaps?: boolean;
  enableExceptions?: boolean;
  enableGaps?: boolean;
  showRegionStatusSelectors?: boolean;
}

type CombinedFiltersProps = DetailsFiltersProps & CountyProjectContextProps & CleanHistoryProps;

const AdequacyViewFilter = {
  All: "all",
  Exceptions: "exceptions",
  Gaps: "gaps",
} as const;

type AdequacyViewFilterValue = typeof AdequacyViewFilter[keyof typeof AdequacyViewFilter];

const DetailsFiltersContainer = (props: DetailsFiltersProps) => {
  const projectContext = useContext(CountyProjectContext);
  const pushOrReplaceLocation = useCleanHistory();
  return <DetailsFilters {...props} {...projectContext} pushOrReplaceLocation={pushOrReplaceLocation} />;
};

class DetailsFilters extends Component<CombinedFiltersProps> {
  componentDidUpdate(prevProps: CombinedFiltersProps) {
    if (prevProps.location.search === this.props.location.search && prevProps.perspective === this.props.perspective) {
      return;
    }
    const prevQuery = parseQuerystring(prevProps.location.search) as AdequacyQueryShape;
    const currQuery = parseQuerystring(this.props.location.search) as AdequacyQueryShape;

    const perspectiveChanged = prevProps.perspective !== this.props.perspective;

    if ((prevQuery.changedGaps !== currQuery.changedGaps || perspectiveChanged) && currQuery.changedGaps && (!this.props.compareToRunConfiguration || this.props.perspective !== ProjectPerspective.All)) {
      this.updateQuery(AdequacyViewFilter.All);
    }

    if ((prevQuery.specialtyType !== currQuery.specialtyType || perspectiveChanged) && currQuery.specialtyType && this.props.perspective !== ProjectPerspective.All && this.props.perspective !== ProjectPerspective.BySpecialty) {
      this.props.history.replace({
        ...this.props.history.location,
        search: querystringify({ ...currQuery, specialtyType: null }),
      });
    }
  }

  public render() {
    if (this.props.regionFilters.county && this.props.regionFilters.specialty) {
      //don't allow filtering once the user is already viewing a region-set-specialty-group summary result
      return null;
    }
    const query = parseQuerystring(this.props.location.search) as AdequacyQueryShape;
    const activeFilter: AdequacyViewFilterValue =
      query.gaps === "true" ? AdequacyViewFilter.Gaps :
      query.exceptions === "true" ? AdequacyViewFilter.Exceptions :
      AdequacyViewFilter.All;

    const availableRegionSetStatusTypes =
      this.props.adequacyResult
        ? this.props.compareToAdequacyResult
          ? union(this.props.adequacyResult.availableRegionSetStatusTypes, this.props.compareToAdequacyResult.availableRegionSetStatusTypes)
          : this.props.adequacyResult.availableRegionSetStatusTypes
        : this.props.availableRegionFilters || [];

    const isShowingAggregates = !this.props.regionFilters.county && !this.props.regionFilters.specialty && this.props.perspective !== ProjectPerspective.All;

    const gapsDisabled = !this.props.enableGaps &&
      (this.props.loading || this.props.loadingResults ||
        (!(this.props.adequacyFilters && this.props.adequacyFilters.gaps)
          && !(this.props.adequacyResult && this.props.adequacyResult.hasGaps)
          && !(this.props.compareToAdequacyResult && this.props.compareToAdequacyResult.hasGaps))) ||
      (this.props.cmsCreditShowOnlyChanges && this.props.cmsCreditIsApplying) ||
      (this.props.cmsCreditIsApplying && isShowingAggregates &&
        (this.props.adequacyResult.aggregateSummary.percentPassBoth === 100 || this.props.adequacyResult.aggregateSummary.percentPassBothCMSCredit === 100)) ||
      (this.props.cmsCreditIsApplying && !isShowingAggregates &&
        this.props.adequacyResult.details.length > 0 &&
        this.props.adequacyResult.details.every((d) => (d.applicationCreditMetCriteria || d.metCriteria)));

    const exceptionsDisabled = !this.props.enableExceptions && (this.props.loading || this.props.loadingResults || !(this.props.adequacyFilters && this.props.adequacyFilters.exceptions) && !(this.props.adequacyResult && this.props.adequacyResult.hasExceptions));
    const changedGapsDisabled = !this.props.enableChangedGaps;
    const specialtyFilterDisabled = this.props.perspective !== ProjectPerspective.All && this.props.perspective !== ProjectPerspective.BySpecialty;

    const allDisabled = (this.props.loading || this.props.loadingResults) && !this.props.project.isReadOnly;

    const perspectiveDropdownItems = this.props.availablePerspectives.map((perspective) => ({
      key: perspective,
      name: getProjectPerspectiveDescription(perspective),
    }));

    this.handleInvalidViewBy();

    return (
      <div className="details-filters">
        <span className="pr10">
          {!this.props.regionFilters.county && perspectiveDropdownItems.length && (
            <NewActionMenu
              buttonText={`View by: ${perspectiveDropdownItems.find((item) => item.key === this.props.perspective)?.name}`}
              testId="details-filters__view-by-dropdown"
              buttonClassNames="qes-button-secondary"
              loading={this.props.disabled || this.props.loadingResults || this.props.loading}
              // force a rerender on a perspective change
              key={this.props.perspective}
            >
              {
                perspectiveDropdownItems.map((item) => {
                  const isSelected = item.key === this.props.perspective;
                  return (
                    <NewActionMenuItem
                      display={item.name}
                      isSelected={isSelected}
                      key={item.key}
                      onClick={() => this.setPerspective(item.key)}
                      testId={`details-filters__view-by-option--${item.key}`}
                    />
                  )
                })
              }
            </NewActionMenu>
          )}
        </span>

        <div>
          <div className="adequacy-toggle-container">
            <QESRadioGroup renderInline={true}>
              <QESRadioButton
                label="All"
                checked={activeFilter === AdequacyViewFilter.All}
                onChange={() => this.updateQuery(AdequacyViewFilter.All)}
                disabled={allDisabled}
                testId="details-filter__details-filters-all-icon-toggle"
                labelClass="adequacy-view-filter-label"
                radioContainerClassName="adequacy-view-filter-radio-button"
              />
              <TooltipWrapper
                tooltipWidth="150px"
                text={gapsDisabled ? "This filter is disabled because there is no data to display for GAPS" : ""}
                containerClassName="d-inline-flex align-items-center"
              >
                <QESRadioButton
                  label="Gaps"
                  checked={activeFilter === AdequacyViewFilter.Gaps}
                  onChange={() => this.updateQuery(AdequacyViewFilter.Gaps)}
                  disabled={gapsDisabled || allDisabled}
                  testId="details-filter__details-filters-gaps-icon-toggle"
                  labelClass="adequacy-view-filter-label"
                  radioContainerClassName="adequacy-view-filter-radio-button"
                />
              </TooltipWrapper>
              <TooltipWrapper
                tooltipWidth="150px"
                text={exceptionsDisabled ? "This filter is disabled because there is no data to display for EXCEPTIONS" : ""}
                containerClassName="d-inline-flex align-items-center"
              >
                <QESRadioButton
                  label="Exceptions"
                  checked={activeFilter === AdequacyViewFilter.Exceptions}
                  onChange={() => this.updateQuery(AdequacyViewFilter.Exceptions)}
                  disabled={exceptionsDisabled || allDisabled}
                  testId="details-filter__details-filters-exceptions-icon-toggle"
                  labelClass="adequacy-view-filter-label"
                  radioContainerClassName="adequacy-view-filter-radio-button"
                />
              </TooltipWrapper>
            </QESRadioGroup>
          </div>
          <NewActionMenu
            buttonClassNames="qes-button qes-button-secondary h-100"
            buttonText={changedGapsDisabled || allDisabled
              ? (
                <TooltipWrapper tooltipWidth="150px"
                                text={"This filter is only available when viewing by \"All\" and comparing results that have changed gaps"}>
                  Changed Gaps
                </TooltipWrapper>
              )
              : (<> Changed Gaps </>)
            }
            alignRight={true}
            testId="details-filters__county-changed-gaps-dropdown"
            disabled={changedGapsDisabled || allDisabled}
          >
            <ChangedGapsFilter/>
          </NewActionMenu>
          <NewActionMenu
            buttonClassNames="qes-button qes-button-secondary h-100"
            buttonText={specialtyFilterDisabled || allDisabled
              ? (
                <TooltipWrapper tooltipWidth="150px"
                  text={"This filter is only available when viewing by \"All\" or viewing by \"Specialty\""}>
                  Specialty Filter
                </TooltipWrapper>
              )
              : (<> Specialty Filter </>)
            }
            alignRight={true}
            testId="details-filters__county-specialties"
            disabled={specialtyFilterDisabled || allDisabled}
          >
            <SpecialtyFilter />
          </NewActionMenu>
          <NewActionMenu
            buttonClassNames="qes-button qes-button-secondary h-100"
            buttonText={<><QesIcon iconName={QesIcons.FilterLines} isDecorative className="align-text-top me-1" /> Status Filters </>}
            alignRight={true}
            testId="details-filters__county-status-dropdown"
            disabled={allDisabled || !!this.props.regionFilters.county}
          >
            <RegionStatusFilter availableRegionSetStatusTypes={availableRegionSetStatusTypes}
                                showRegionStatusSelectors={this.props.showRegionStatusSelectors}/>
          </NewActionMenu>
        </div>
      </div>
    );
  }

  private setPerspectiveFromFilter = (filter: string) => {
    const perspective = getProjectPerspectiveFromFilterString(filter);
    this.setPerspective(perspective);
  }

  private setPerspective = (perspective: ProjectPerspective) => {
    this.props.setPerspective(perspective);
    const regionFilter = this.props.regionFilters?.region;
    if (perspective !== ProjectPerspective.ByCounty && regionFilter) {
      const cleanPathname = this.props.location.pathname
        .replace(`${FilterType.Region}${regionFilter}`, "");
      this.props.pushOrReplaceLocation({
        pathname: cleanPathname,
        search: querystringify({
          ...parseQuerystring(this.props.location.search),
          viewBy: perspective,
        }),
      });
    } else {
      this.updatePerspectiveQueryParam(perspective);
    }
  }

  private updatePerspectiveQueryParam = (perspective: ProjectPerspective) => {
    const query = parseQuerystring(this.props.location.search);
    const newQuery = {
      ...query,
      viewBy: perspective,
    };
    const qs = querystringify(newQuery);
    this.props.pushOrReplaceLocation({
      search: qs
    });
  }

  private updateQuery = (selected: AdequacyViewFilterValue) => {
    let query = parseQuerystring(this.props.location.search) as AdequacyQueryShape;
    query = {
      ...query,
      all: selected === AdequacyViewFilter.All ? "true" : "false",
      changedGaps: null,
      exceptions: selected === AdequacyViewFilter.Exceptions ? "true" : "false",
      gaps: selected === AdequacyViewFilter.Gaps ? "true" : "false",
    };

    const qs = querystringify(query as { [key: string]: unknown });
    this.props.pushOrReplaceLocation({ search: qs });
  }

  private handleInvalidViewBy = () => {
    if (!(this.props.perspective && !Object.values(ProjectPerspective).includes(this.props.perspective))) {
      return;
    }

    // If we find an invalid perspective, remove it from the URL
    const searchParams = new URLSearchParams(this.props.location.search);
    searchParams.delete('viewBy');
    this.props.history.replace({ ...this.props.location, search: searchParams.toString() });
  }
}

const WrappedDetailsFilters = withRouter(DetailsFiltersContainer);

export default WrappedDetailsFilters;
